import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "../ui/sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { formatDate, formatDateTime } from "../../lib/dateUtils";

const RECIPIENT_TYPES = [
  { value: "user", label: "Single User" },
  { value: "session_participants", label: "All Session Participants" },
  { value: "photographer", label: "Photographer" },
  { value: "matched_pair", label: "Matched Pair" },
];

const MESSAGE_TYPE_LABELS = {
  admin_message: "Admin Message",
  session_announcement: "Session Announcement",
  cancellation: "Cancellation Notice",
  reminder: "Reminder",
  system_notification: "System",
  photographer_message: "Photographer Message",
  matching_notification: "Matching",
  gallery_ready: "Gallery Ready",
};

const EMPTY_FORM = { recipient_type: "user", recipient_id: "", session_id: "", match_id: "", message_type: "admin_message", subject: "", content: "", send_email_copy: true };

const ComposePanel = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUserLabel, setSelectedUserLabel] = useState("");
  const [photographers, setPhotographers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/photographers").then(({ data }) => setPhotographers(data));
    api.get("/admin/sessions").then(({ data }) => setSessions(data));
    api.get("/admin/applications").then(({ data }) => {
      const matched = data.filter((a) => a.status === "matched" && a.match_id);
      const grouped = {};
      matched.forEach((a) => {
        if (!grouped[a.match_id]) grouped[a.match_id] = { match_id: a.match_id, session_title: a.session_title, session_date: a.session_date, members: [] };
        grouped[a.match_id].members.push(a);
      });
      setMatches(Object.values(grouped));
    });
  }, []);

  useEffect(() => {
    if (form.recipient_type !== "user" || userSearch.trim().length < 2) {
      setUserResults([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/admin/users", { params: { search: userSearch } }).then(({ data }) => setUserResults(data.slice(0, 8)));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, form.recipient_type]);

  const send = async () => {
    setSending(true);
    try {
      await api.post("/admin/messages/send", form);
      toast.success("Message sent");
      setForm(EMPTY_FORM);
      setSelectedUserLabel("");
      setUserSearch("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const canSend =
    form.subject.trim() &&
    form.content.trim() &&
    ((form.recipient_type === "user" && form.recipient_id) ||
      (form.recipient_type === "photographer" && form.recipient_id) ||
      (form.recipient_type === "session_participants" && form.session_id) ||
      (form.recipient_type === "matched_pair" && form.match_id));

  return (
    <div className="max-w-xl space-y-4" data-testid="compose-message-panel">
      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Send To</label>
        <Select value={form.recipient_type} onValueChange={(v) => setForm({ ...EMPTY_FORM, recipient_type: v, message_type: form.message_type })}>
          <SelectTrigger data-testid="compose-recipient-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RECIPIENT_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {form.recipient_type === "user" && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Search by name or email</label>
          <Input data-testid="compose-user-search-input" placeholder="Type at least 2 characters..." value={selectedUserLabel || userSearch} onChange={(e) => { setUserSearch(e.target.value); setSelectedUserLabel(""); setForm({ ...form, recipient_id: "" }); }} />
          {userResults.length > 0 && !form.recipient_id && (
            <div className="border border-soft-gray rounded-xl mt-2 divide-y divide-soft-gray" data-testid="compose-user-results">
              {userResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  data-testid={`compose-user-result-${u.id}`}
                  onClick={() => { setForm({ ...form, recipient_id: u.id }); setSelectedUserLabel(`${u.first_name} ${u.last_name} (${u.email})`); setUserResults([]); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-warm-white"
                >
                  {u.first_name} {u.last_name} <span className="text-midnight-black/50">· {u.role} · {u.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {form.recipient_type === "photographer" && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Photographer</label>
          <Select value={form.recipient_id} onValueChange={(v) => setForm({ ...form, recipient_id: v })}>
            <SelectTrigger data-testid="compose-photographer-select"><SelectValue placeholder="Choose a photographer" /></SelectTrigger>
            <SelectContent>
              {photographers.map((p) => <SelectItem key={p.user_id} value={p.user_id}>{p.business_name || p.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {form.recipient_type === "session_participants" && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Session</label>
          <Select value={form.session_id} onValueChange={(v) => setForm({ ...form, session_id: v })}>
            <SelectTrigger data-testid="compose-session-select"><SelectValue placeholder="Choose a session" /></SelectTrigger>
            <SelectContent>
              {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.title} — {formatDate(s.session_date)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {form.recipient_type === "matched_pair" && (
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Matched Pair</label>
          <Select value={form.match_id} onValueChange={(v) => setForm({ ...form, match_id: v })}>
            <SelectTrigger data-testid="compose-match-select"><SelectValue placeholder="Choose a matched pair" /></SelectTrigger>
            <SelectContent>
              {matches.map((m) => <SelectItem key={m.match_id} value={m.match_id}>{m.members.map((mem) => mem.stranger_name).join(" + ")} — {m.session_title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Message Type</label>
        <Select value={form.message_type} onValueChange={(v) => setForm({ ...form, message_type: v })}>
          <SelectTrigger data-testid="compose-message-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="admin_message">Admin Message</SelectItem>
            <SelectItem value="session_announcement">Session Announcement</SelectItem>
            <SelectItem value="cancellation">Cancellation Notice</SelectItem>
            <SelectItem value="reminder">Reminder</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Input data-testid="compose-subject-input" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
      <Textarea data-testid="compose-content-input" placeholder="Message content" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />

      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" data-testid="compose-send-email-checkbox" checked={form.send_email_copy} onChange={(e) => setForm({ ...form, send_email_copy: e.target.checked })} />
        Also send as email
      </label>

      <Button data-testid="compose-send-button" disabled={!canSend || sending} onClick={send} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8">
        {sending ? "Sending..." : "SEND MESSAGE"}
      </Button>
    </div>
  );
};

const MessageLog = () => {
  const [logs, setLogs] = useState(null);
  const [messageType, setMessageType] = useState("");

  const load = () => api.get("/admin/messages", { params: { message_type: messageType || undefined } }).then(({ data }) => setLogs(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageType]);

  return (
    <div data-testid="message-log-panel">
      <div className="w-56 mb-6">
        <Select value={messageType || "all"} onValueChange={(v) => setMessageType(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="message-log-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Message Types</SelectItem>
            {Object.entries(MESSAGE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {logs === null ? (
        <p className="font-medium text-midnight-black/60">Loading message log...</p>
      ) : logs.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-message-logs">No messages sent yet.</p>
      ) : (
        <div className="space-y-2" data-testid="message-log-list">
          {logs.map((m) => (
            <div key={m.id} className="border border-soft-gray rounded-xl p-4 text-sm" data-testid={`message-log-row-${m.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-bold">{m.subject}</p>
                <span className="text-[9px] font-bold uppercase tracking-wide text-stranger-orange">{MESSAGE_TYPE_LABELS[m.message_type] || m.message_type}</span>
              </div>
              <p className="text-xs text-midnight-black/60 mt-1">{m.content}</p>
              <p className="text-[10px] text-midnight-black/40 mt-2">
                From {m.sender_name || "System"} ({m.sender_role}) to {m.recipient_name || m.recipient_email} {m.session_title && `· ${m.session_title}`} · {formatDateTime(m.created_at)} · {m.read ? "Read" : "Unread"}{m.email_sent && " · Emailed"}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MessagesTab = () => (
  <div data-testid="admin-messages-tab">
    <Tabs defaultValue="compose">
      <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-8" data-testid="messages-sub-tabs">
        <TabsTrigger value="compose" data-testid="messages-subtab-compose" className="text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 border-soft-gray data-[state=active]:bg-stranger-orange data-[state=active]:text-clean-white data-[state=active]:border-stranger-orange">
          Compose
        </TabsTrigger>
        <TabsTrigger value="log" data-testid="messages-subtab-log" className="text-xs font-bold uppercase tracking-wide px-4 py-2 rounded-full border-2 border-soft-gray data-[state=active]:bg-stranger-orange data-[state=active]:text-clean-white data-[state=active]:border-stranger-orange">
          Message Log
        </TabsTrigger>
      </TabsList>
      <TabsContent value="compose"><ComposePanel /></TabsContent>
      <TabsContent value="log"><MessageLog /></TabsContent>
    </Tabs>
  </div>
);
